import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Event, EventView, GlobalAccount, GUID } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { Toast } from 'widgets';

export const useGetEvent = (eventGUID: GUID<Event> | null) => {
    return useQuery({
        queryKey: ['event', eventGUID],
        enabled: !!eventGUID,
        queryFn: async () => {
            const [eventView] = await CustomerApi().getEvent(eventGUID! as unknown as GUID<EventView>);
            return eventView ?? null;
        },
    });
};

export const useListHouseholdEvents = ({ householdGUID }: { householdGUID: GUID<GlobalAccount> }) => {
    return useQuery({
        queryKey: ['events', householdGUID],
        enabled: !!householdGUID,
        queryFn: async () => {
            const [eventViews] = await CustomerApi().listHouseholdEvents(householdGUID);
            return eventViews || [];
        },
    });
};

export const useGetMeetingBot = (householdGUID: string, eventGUID: string, enabled = true) => {
    const queryClient = useQueryClient();

    return useQuery({
        enabled,
        refetchInterval: enabled ? 5 * 1000 : false,
        queryKey: ['event-view', eventGUID, 'meeting-bot'],
        queryFn: async () => {
            const [response] = await CustomerApi().getMeetingBot(eventGUID);
            if (response?.status === 'COMPLETED') {
                queryClient.invalidateQueries({
                    queryKey: ['Notes', householdGUID, eventGUID],
                });
                queryClient.invalidateQueries({
                    queryKey: ['NotesByEvent', eventGUID],
                });
            }
            return response;
        },
    });
};

export const useInviteMeetingBot = (eventGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await CustomerApi({ hideError: true }).inviteMeetingBot(eventGUID);
        },
        onSuccess: () => {
            Toast.success('Claire™ joining');
            queryClient.invalidateQueries({
                queryKey: ['event-view', eventGUID],
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to invite Claire™');
        },
    });
};

export const useScheduleMeetingBot = (eventGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await CustomerApi({ hideError: true }).scheduleMeetingBot(eventGUID);
        },
        onSuccess: () => {
            Toast.success('Claire™ scheduled');
            queryClient.invalidateQueries({
                queryKey: ['event-view', eventGUID],
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to schedule Claire™');
        },
    });
};

export const useAssignEventHousehold = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            eventGUID,
            householdGUID,
        }: {
            eventGUID: GUID<EventView>;
            householdGUID: GUID<GlobalAccount>;
        }) => {
            await CustomerApi({ hideError: true }).assignHousehold(eventGUID, householdGUID);
        },
        onSuccess: (_, { eventGUID }) => {
            Toast.success('Household updated');
            queryClient.invalidateQueries({ queryKey: ['events'] });
            queryClient.invalidateQueries({ queryKey: ['event', eventGUID] });
            queryClient.invalidateQueries({
                predicate: ({ queryKey }) => queryKey.includes('calendar') && queryKey.includes('views'),
            });
            queryClient.invalidateQueries({ queryKey: ['Notes'] });
            queryClient.invalidateQueries({ queryKey: ['NotesByEvent'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to update household');
        },
    });
};

export const useRemoveMeetingBot = (eventGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await CustomerApi({ hideError: true }).removeMeetingBot(eventGUID);
        },
        onSuccess: () => {
            Toast.success('Claire™ has been removed');
            queryClient.invalidateQueries({
                queryKey: ['event-view', eventGUID],
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to remove Claire™');
        },
    });
};
